import clsx from "clsx";
import type { KalkStundenAppEntry } from "@/lib/projects";
import {
  formatHours,
  groupKalkStundenForDisplay,
  kalkStundenChipLabel,
  type KalkStundenSichtbarkeit,
} from "@/pages/projekte/hoursUsage";

function SichtbarkeitBadge({
  value,
  compact = false,
}: {
  value: Exclude<KalkStundenSichtbarkeit, "mixed">;
  compact?: boolean;
}) {
  const isBuero = value === "buero";
  return (
    <span
      className={clsx(
        "eh-po-kalk-badge",
        isBuero ? "eh-po-kalk-badge--buero" : "eh-po-kalk-badge--alle",
        compact && "eh-po-kalk-badge--compact",
      )}
      title={isBuero ? "Nur für das Büro sichtbar" : "Sichtbar für alle"}
    >
      {isBuero ? "Nur Büro" : "Alle"}
    </span>
  );
}

export function KalkStundenOverview({
  items,
  title = "Kalkulierte Festpreis-Stunden",
  heading = "label",
}: {
  items: KalkStundenAppEntry[];
  title?: string;
  heading?: "label" | "section";
}) {
  const groups = groupKalkStundenForDisplay(items);
  if (!groups.length) return null;

  const showBadges = groups.some((group) => group.sichtbarkeit !== "alle");

  return (
    <div className="eh-po-kalk-stunden">
      {title ? (
        heading === "section" ? (
          <h2 className="mb-3 text-base font-semibold text-label">{title}</h2>
        ) : (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            {title}
          </p>
        )
      ) : null}

      <div className="eh-po-kalk-groups">
        {groups.map((group) => (
          <div key={group.id} className={clsx("eh-po-kalk-group", `eh-po-kalk-group--${group.id}`)}>
            <div className="eh-po-kalk-group-head">
              <div className="eh-po-kalk-group-title">
                <span>{group.title}</span>
                {showBadges && group.sichtbarkeit !== "mixed" ? (
                  <SichtbarkeitBadge value={group.sichtbarkeit} />
                ) : null}
              </div>
              <span className="eh-po-kalk-group-total">{formatHours(group.total)}</span>
            </div>
            <ul className="eh-po-kalk-rows">
              {group.items.map((item) => (
                <li key={`${item.lohnart_id}-${item.bezeichnung}`} className="eh-po-kalk-row">
                  <span className="eh-po-kalk-row-label">
                    {kalkStundenChipLabel(item, group.id)}
                    {showBadges && group.sichtbarkeit === "mixed" ? (
                      <SichtbarkeitBadge
                        value={item.sichtbarkeit === "buero" ? "buero" : "alle"}
                        compact
                      />
                    ) : null}
                  </span>
                  <strong>{formatHours(item.stunden)}</strong>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
